import { faCloudSunRain, faUmbrella, faWind } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Result } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import Helper from "../../Helper";
import Weather from "../../Weather";
import { DailyForecast } from "../WeatherResponseTypes";

interface Props {
    flexDirection: string;
    backgroundColor?: string;
    textColor?: string;
    headerTextColor?: string;
    headerFontSize?: string;
    weather: any;
    project: any;
}

const Next5Days: React.FC<Props> = (props) => {
    const isMobile = Helper.isMobileDevice();

    const [upcomingHigh, setUpcomingHigh] = useState(0);
    const [upcomingLow, setUpcomingLow] = useState(0);

    const [dailyForecast, setDailyForecast] = useState<DailyForecast[]>([]);

    useEffect(() => {
        if (props.weather?.daily_forecast) {
            setDailyForecast(props.weather.daily_forecast);
        }
    }, [props.weather]);

    // Compute min/max across the next 5 days (excluding "today" entry at index 0 to match old behavior)
    useEffect(() => {
        if (!dailyForecast.length) return;

        const nextFive = dailyForecast.slice(1, 6);
        if (!nextFive.length) return;

        const mins = nextFive.map((d) => d.low_temp);
        const maxs = nextFive.map((d) => d.high_temp);

        setUpcomingLow(Math.min(...mins));
        setUpcomingHigh(Math.max(...maxs));
    }, [dailyForecast]);

    const hasDaily = useMemo(() => dailyForecast && dailyForecast.length > 0, [dailyForecast]);

    if (!hasDaily) {
        return (
            <Result
                className={Helper.classNames("w-full h-full")}
                icon={<FontAwesomeIcon icon={faCloudSunRain} size="3x" />}
                title={<div className={props.textColor}>Weather currently unavailable</div>}
            />
        );
    }

    return (
        <div
            className={`flex items-center w-full h-full justify-between my-2 ${
                props.flexDirection.includes("row") ? "flex-wrap" : "flex-nowrap"
            }`}
        >
            {/* Header */}
            <div
                className={Helper.classNames(
                    "flex justify-between items-center font-plot-oswald text-center uppercase",
                    props.headerTextColor ? `text-[${props.headerTextColor}]` : "text-white",
                    props.flexDirection,
                    props.flexDirection.includes("row") && "w-full"
                )}
            >
                <div
                    className={Helper.classNames(
                        "flex basis-1/6 items-center justify-center",
                        props.headerFontSize ? props.headerFontSize : "text-lg"
                    )}
                >
                    Daily
                </div>
                <div className={Helper.classNames("flex basis-1/12 items-center justify-center")} />
                <div
                    className={Helper.classNames(
                        "flex basis-1/6 grow w-full flex-row justify-between",
                        props.headerFontSize ? props.headerFontSize : "text-lg"
                    )}
                >
                    <div>Low</div>
                    <div>High</div>
                </div>
                <div
                    className={Helper.classNames(
                        "flex basis-1/6 items-center justify-center",
                        props.headerFontSize ? props.headerFontSize : "text-lg"
                    )}
                >
                    <FontAwesomeIcon icon={faUmbrella} />
                </div>
                <div
                    className={Helper.classNames(
                        "flex basis-1/6 items-center text-center justify-center whitespace-nowrap font-plot-oswald uppercase",
                        props.headerFontSize ? props.headerFontSize : "text-lg"
                    )}
                >
                    <FontAwesomeIcon icon={faWind} className={Helper.classNames(!isMobile && "mr-1")} />
                    {!isMobile && "Sust. / Gust"}
                </div>
            </div>

            {dailyForecast.map(
                (day, index) =>
                    index < 5 && (
                        <div
                            key={day.date ?? index}
                            className={`flex justify-between items-center ${props.flexDirection} ${
                                props.flexDirection.includes("row") && "w-full"
                            }`}
                        >
                            <div className="flex basis-1/6 flex-row items-center justify-center whitespace-nowrap">
                                {isMobile ? dayjs(day.date).format("ddd D") : dayjs(day.date).format("ddd, MMM D")}
                            </div>

                            <div className="flex basis-1/12 items-center justify-center">
                                <FontAwesomeIcon
                                    icon={Weather.getWeatherIcon(day.condition)}
                                    size={isMobile ? "sm" : "1x"}
                                />
                            </div>

                            <div className="h-1 flex basis-1/6 grow items-center justify-between">
                                <div className="mr-2">{Math.round(day.low_temp)}°</div>
                                <div className="relative w-full h-1 bg-linear-to-r from-blue-500 to-red-500">
                                    <div
                                        style={{
                                            width: Weather.getLeftMarginPercentage(
                                                upcomingHigh,
                                                upcomingLow,
                                                day.low_temp
                                            ),
                                        }}
                                        className={Helper.classNames(
                                            "absolute h-1",
                                            props.backgroundColor
                                                ? `bg-[${props.backgroundColor}]`
                                                : "bg-white dark:bg-plot-slate"
                                        )}
                                    />
                                    <div
                                        style={{
                                            width: Weather.getRightMarginPercentage(
                                                upcomingHigh,
                                                upcomingLow,
                                                day.high_temp
                                            ),
                                        }}
                                        className={Helper.classNames(
                                            "right-0 absolute h-1",
                                            props.backgroundColor
                                                ? `bg-[${props.backgroundColor}]`
                                                : "bg-white dark:bg-plot-slate"
                                        )}
                                    />
                                </div>
                                <div className="ml-2">{Math.round(day.high_temp)}°</div>
                            </div>

                            <div className="flex basis-1/6 flex-row items-center justify-center">
                                {day.precipitation_chance ?? 0}%
                            </div>

                            <div className="flex flex-row items-center justify-center whitespace-nowrap basis-1/6">
                                <div className="flex items-center gap-1 font-roboto-mono">
                                    <span className="inline-block w-4 text-right">
                                        {Math.round(day.wind_speed ?? 0)}
                                    </span>
                                    <span>/</span>
                                    <span className="inline-block w-4 text-left">{Math.round(day.wind_gust ?? 0)}</span>
                                    <span className="ml-1">mph</span>
                                </div>
                            </div>
                        </div>
                    )
            )}
        </div>
    );
};
export default Next5Days;
